import React, { useState, useEffect, useMemo } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import Modal from "react-bootstrap/Modal"
import API from "./../../Util/API"
import { SkeletonCard } from "../../components/SkeletonCard/SkeletonCard"
import { FaSearch, FaFilePdf, FaFolder, FaArrowLeft, FaDownload, FaExternalLinkAlt, FaTimes } from "react-icons/fa"
import "./Posts.css"

export function Posts({ title, path }) {
	const [isLoaded, setIsLoaded] = useState(false)
	const [items, setItems] = useState([])
	const [searchTerm, setSearchTerm] = useState("")
	const location = useLocation()
	const { area } = useParams()
	const api = API()

	const [selectedItemId, setSelectedItemId] = useState("")
	const [selectedItemTitle, setSelectedItemTitle] = useState("")
	const [show, setShow] = useState(false)

	const handleShow = (item) => {
		setSelectedItemId(item.id)
		setSelectedItemTitle(item.title || "Documento PDF")
		setShow(true)
	}

	const handleClose = () => setShow(false)

	useEffect(() => {
		setIsLoaded(false)
		setSearchTerm("")

		if (area) {
			api
				.get(`published/${path}/posts/${area}`)
				.then((response) => {
					setIsLoaded(true)
					setItems(Array.isArray(response) ? response : [])
				})
				.catch((error) => {
					console.error(error)
					setIsLoaded(true)
				})
		} else {
			if (title === "Projetos") {
				api
					.get(`published/${path}/areas`)
					.then((response) => {
						setIsLoaded(true)
						setItems(Array.isArray(response) ? response : [])
					})
					.catch((error) => {
						console.error(error)
						setIsLoaded(true)
					})
			} else {
				api
					.get(`published/${path}`)
					.then((response) => {
						setIsLoaded(true)
						setItems(Array.isArray(response) ? response : [])
					})
					.catch((error) => {
						console.error(error)
						setIsLoaded(true)
					})
			}
		}
	}, [path, location.pathname, area, title])

	const filteredItems = useMemo(() => {
		if (!items) return []
		return items.filter((item) => {
			if (typeof item === "string") {
				return item.toLowerCase().includes(searchTerm.toLowerCase())
			}

			const matchesSearch =
				(item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
				(item.abstract && item.abstract.toLowerCase().includes(searchTerm.toLowerCase()))

			return matchesSearch
		})
	}, [items, searchTerm])

	const pdfUrl = selectedItemId ? `${api.baseURL}/published/${path}/${selectedItemId}/pdf` : ""

	return (
		<React.Fragment>
			<main className="page blog-post-list flex-grow-1 d-flex flex-column">
				<section className="clean-block clean-blog-list pt-4 pt-md-5 pb-5 flex-grow-1">
					<div className="container">
						{/* Breadcrumb Navigation */}
						<nav aria-label="breadcrumb" className="mb-3 pt-2">
							<ol className="breadcrumb">
								<li className="breadcrumb-item">
									<Link to="/" className="text-decoration-none">
										Início
									</Link>
								</li>
								{area ? (
									<>
										<li className="breadcrumb-item">
											<Link to={`/${path}`} className="text-decoration-none">
												{title}
											</Link>
										</li>
										<li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
											{area}
										</li>
									</>
								) : (
									<li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
										{title}
									</li>
								)}
							</ol>
						</nav>

						{/* Header with Back Button if in Area */}
						<div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
							<div>
								<h1 className="h2 fw-bold text-primary mb-1">{area ? `${title}: ${area}` : title}</h1>
								<p className="text-muted small mb-0">
									{title === "Projetos" && !area
										? "Selecione uma área de pesquisa para visualizar os projetos correspondentes."
										: `Explore nossa lista de ${title.toLowerCase()} em ciência de dados e inteligência artificial.`}
								</p>
							</div>

							{area && (
								<Link to={`/${path}`} className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center">
									<FaArrowLeft className="me-2" /> Voltar para Áreas
								</Link>
							)}
						</div>

						{/* Search Bar */}
						<div className="search-filter-panel mb-4 p-3 bg-white rounded-3 shadow-sm border">
							<div className="input-group">
								<span className="input-group-text bg-light border-end-0">
									<FaSearch className="text-muted" />
								</span>
								<input
									type="text"
									className="form-control border-start-0 ps-0"
									placeholder={
										typeof items[0] === "string" ? "Buscar áreas de projetos..." : `Buscar por título, resumo ou palavra-chave em ${title}...`
									}
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
								/>
								{searchTerm && (
									<button className="btn btn-outline-secondary border-start-0" type="button" onClick={() => setSearchTerm("")} title="Limpar busca">
										<FaTimes />
									</button>
								)}
							</div>

							{/* Active query status */}
							<div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top text-muted small">
								<span>
									Mostrando <strong>{filteredItems.length}</strong> de <strong>{items.length}</strong> {items.length === 1 ? "item" : "itens"}
								</span>
								{searchTerm && (
									<button className="btn btn-link btn-sm p-0 text-decoration-none text-danger" onClick={() => setSearchTerm("")}>
										Limpar busca
									</button>
								)}
							</div>
						</div>

						{/* Content Area */}
						{!isLoaded ? (
							<SkeletonCard count={6} />
						) : filteredItems.length === 0 ? (
							<div className="empty-state text-center py-5 bg-white rounded-3 shadow-sm border p-4">
								<div className="empty-icon mb-3 text-muted">
									<FaSearch size={48} className="opacity-25" />
								</div>
								<h3 className="h4 text-dark fw-bold mb-2">Nenhum resultado encontrado</h3>
								<p className="text-muted mb-4 max-w-md mx-auto">
									Não encontramos nenhum item correspondente aos critérios selecionados. Tente ajustar os termos de busca ou limpar os filtros.
								</p>
								<button className="btn btn-primary px-4 rounded-pill" onClick={() => setSearchTerm("")}>
									Limpar busca e ver todos
								</button>
							</div>
						) : (
							<div className="posts-container">
								{filteredItems.map((item) => {
									if (title === "Projetos" && !area && typeof item === "string") {
										return (
											<div className="clean-blog-post-card card mb-3 border-0 shadow-sm rounded-3 overflow-hidden" key={item}>
												<div className="card-body p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
													<div className="d-flex align-items-center gap-3">
														<div className="area-icon-box bg-primary-subtle text-primary p-3 rounded-circle d-flex align-items-center justify-content-center">
															<FaFolder size={24} />
														</div>
														<div>
															<h2 className="h4 fw-bold text-dark mb-1">{item}</h2>
															<span className="badge bg-light text-secondary border">Área de Atuação</span>
														</div>
													</div>
													<Link to={`/${path}/${item}`} state={{ area: item }} className="btn btn-primary px-4 py-2 rounded-pill fw-semibold">
														Ver projetos da área &rarr;
													</Link>
												</div>
											</div>
										)
									}

									// Standard item card (Project in Area, Publication, Resource)
									return (
										<div className="clean-blog-post-card card mb-4 border-0 shadow-sm rounded-3 overflow-hidden" key={item.id}>
											<div className="card-body p-3 p-md-4">
												{item.imageCover ? (
													<div className="row g-4 align-items-center flex-column-reverse flex-lg-row">
														<div className="col-12 col-lg-8">
															<h2 className="h4 fw-bold text-dark mb-2 post-card-title">{item.title}</h2>

															<p className="card-text text-muted mb-4 post-card-abstract">{item.abstract || "Sem resumo disponível."}</p>

															<div className="d-flex align-items-center gap-2 flex-wrap">
																{item.pdfName ? (
																	<button
																		onClick={() => handleShow(item)}
																		className="btn btn-outline-primary px-4 py-2 rounded-pill fw-semibold d-inline-flex align-items-center gap-2"
																	>
																		<FaFilePdf /> Visualizar PDF
																	</button>
																) : (
																	<Link to={`/post/${path}/${item.id}`} className="btn btn-primary px-4 py-2 rounded-pill fw-semibold">
																		Ler artigo completo &rarr;
																	</Link>
																)}
															</div>
														</div>

														<div className="col-12 col-lg-4 text-center">
															<img className="img-fluid rounded-3 post-card-cover shadow-sm" src={item.imageCover} alt={item.title} />
														</div>
													</div>
												) : (
													<div>
														<h2 className="h4 fw-bold text-dark mb-2 post-card-title">{item.title}</h2>

														<p className="card-text text-muted mb-4 post-card-abstract">{item.abstract || "Sem resumo disponível."}</p>

														<div className="d-flex align-items-center gap-2 flex-wrap">
															{item.pdfName ? (
																<button
																	onClick={() => handleShow(item)}
																	className="btn btn-outline-primary px-4 py-2 rounded-pill fw-semibold d-inline-flex align-items-center gap-2"
																>
																	<FaFilePdf /> Visualizar PDF
																</button>
															) : (
																<Link to={`/post/${path}/${item.id}`} className="btn btn-primary px-4 py-2 rounded-pill fw-semibold">
																	Ler artigo completo &rarr;
																</Link>
															)}
														</div>
													</div>
												)}
											</div>
										</div>
									)
								})}
							</div>
						)}
					</div>
				</section>
			</main>

			{/* Enhanced PDF Modal with Universal Actions */}
			<Modal show={show} onHide={handleClose} size="xl" centered dialogClassName="pdf-modal-dialog">
				<Modal.Header closeButton className="bg-light">
					<Modal.Title className="fs-6 fw-bold text-dark d-flex align-items-center gap-2 text-truncate pe-3">
						<FaFilePdf className="text-danger flex-shrink-0" />
						<span className="text-truncate">{selectedItemTitle}</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="p-0 bg-dark text-center position-relative">
					<div className="pdf-modal-actions bg-light border-bottom p-2 d-flex justify-content-end gap-2 px-3">
						<a href={pdfUrl} download className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1" title="Baixar arquivo PDF">
							<FaDownload /> Baixar PDF
						</a>
						<a
							href={pdfUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
							title="Abrir PDF em nova aba"
						>
							<FaExternalLinkAlt /> Abrir em Nova Aba
						</a>
					</div>

					<div className="pdf-embed-wrapper">
						<object data={pdfUrl} type="application/pdf" className="pdf-object">
							<div className="p-5 text-white bg-dark">
								<p className="mb-3">Seu navegador não suporta visualização inline de PDF neste formato.</p>
								<div className="d-flex justify-content-center gap-3">
									<a href={pdfUrl} download className="btn btn-primary">
										<FaDownload className="me-2" /> Baixar PDF
									</a>
									<a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-light">
										<FaExternalLinkAlt className="me-2" /> Abrir em Nova Aba
									</a>
								</div>
							</div>
						</object>
					</div>
				</Modal.Body>
			</Modal>
		</React.Fragment>
	)
}
